import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AppConstants } from 'src/app/commons/app-constants';
import { NetworkStatusService } from './network-status.service';
import { TranslationService } from '../i18n/translation.service';
import { OutboxService } from '../offline/outbox.service';
import { offlineDb, scopedKey } from '../offline/offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';

export interface CompanyMembership {
  companyId: number;
  companyName: string;
  companyLogoUrl: string | null;
  role: string;
  isDefault: boolean;
  isActive: boolean;
}

export interface MyMembershipsResponse {
  enabled: boolean;
  memberships: CompanyMembership[];
}

/**
 * Multi-company membership: list the companies the current user belongs to, and switch the
 * active session to one of them. Backend returns { enabled: false, memberships: [] } when
 * app.feature.multiCompanyMembership is off — callers should treat that as "feature not
 * available" rather than "user has no memberships".
 */
@Injectable({
  providedIn: 'root'
})
export class CompanyMembershipService {
  private apiUrl = AppConstants.API_URL;

  constructor(
    private http: HttpClient,
    private networkStatusService: NetworkStatusService,
    private translationService: TranslationService,
    private outboxService: OutboxService,
  ) { }

  /**
   * True while this device still holds offline writes that belong to the CURRENT tenant.
   * Switching company with work queued is not safe in either direction: replaying it after the
   * switch would create the old tenant's records inside the new one, and dropping it would
   * silently destroy real user work. So the switch is refused until the queue drains — see the
   * callers in HeaderComponent/SideBarComponent, and the wipe branch in
   * offline-db.ts's wipeOfflineDataIfDifferentUser that relies on this being empty.
   *
   * Counts pending + inflight only (see OutboxService.countUnsynced) — a 'rejected' entry is
   * terminal and would otherwise block switching permanently.
   */
  async hasUnsyncedWork(): Promise<boolean> {
    return (await this.outboxService.countUnsynced()) > 0;
  }

  // Write-through cache, same pattern as SideBarMenuService.getSideBarMenu(): the header
  // avatar dropdown / sidebar identity card need this to survive a cold offline navigation,
  // otherwise the "active company" name row silently disappears while companyId (cached
  // separately via the encrypted user-profile blob) keeps showing — confirmed real bug, this
  // call previously had no offline fallback at all while the sibling menu call did.
  private static readonly MEMBERSHIPS_CACHE_KEY = 'companyMemberships';

  getMyMemberships(): Observable<MyMembershipsResponse> {
    return this.http.get<MyMembershipsResponse>(`${this.apiUrl}api/v1/companies/my-memberships`).pipe(
      tap((data) => {
        if (isOfflineCapableNative()) {
          offlineDb.masterCache.put({ key: scopedKey(CompanyMembershipService.MEMBERSHIPS_CACHE_KEY), data, fetchedAt: Date.now() });
        }
      }),
      catchError((err) => (isOfflineCapableNative()
        ? from(offlineDb.masterCache.get(scopedKey(CompanyMembershipService.MEMBERSHIPS_CACHE_KEY))).pipe(
            switchMap((entry) => of((entry?.data as MyMembershipsResponse) ?? { enabled: false, memberships: [] })),
          )
        : throwError(() => err))),
    );
  }

  switchCompany(companyId: number): Observable<{ message: string; companyId: number }> {
    return this.http.post<{ message: string; companyId: number }>(
      `${this.apiUrl}api/v1/companies/switch`, { companyId });
  }

  /** Self-service — ends the caller's own membership in a company they belong to via
   *  multi-company access. Cannot be used on their own default/home company (backend rejects). */
  leaveCompany(companyId: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}api/v1/company/my-membership/${companyId}`);
  }

  /**
   * When two companies in the same membership list share a display name (e.g. same legal
   * entity registered twice under different companyId codes), disambiguates with " - N" so
   * they're not visually indistinguishable in a switcher UI. Order is stable — driven by the
   * memberships array's own order, not re-sorted. Shared by every UI surface that renders a
   * company switcher (header avatar menu, sidebar drawer) so the naming logic has one source
   * of truth instead of drifting between them.
   */
  getDisambiguatedName(memberships: CompanyMembership[], membership: CompanyMembership): string {
    const sameName = memberships.filter(m => m.companyName === membership.companyName);
    if (sameName.length <= 1) {
      return membership.companyName;
    }
    const index = sameName.indexOf(membership);
    return `${membership.companyName} - ${index + 1}`;
  }

  /**
   * Builds the "Switch Company" section as a FLAT list (a disabled section-label item, then
   * one plain item per company) rather than a PrimeNG nested-submenu group. Deliberately flat:
   * relying on p-menu's built-in nested-items rendering gave the section label and the company
   * rows identical visual weight to top-level actions like "Logout" — no way to tell "this is
   * a group heading" from "this is a selectable row" apart. Flat items with their own
   * styleClass give full, reliable CSS control (see .tms-menu-section-label /
   * .tms-menu-company-item in header.component.css and side-bar.component.css) instead of
   * depending on PrimeNG's internal submenu-header markup, which varies across versions.
   * Returns [] when there's nothing to switch between (0 or 1 membership) — callers own any
   * surrounding separators, since whether one's needed depends on what's adjacent.
   */
  buildSwitchCompanyMenuItems(
    memberships: CompanyMembership[],
    sectionLabel: string,
    onSwitch: (companyId: number) => void,
  ): MenuItem[] {
    if (!memberships || memberships.length <= 1) {
      return [];
    }
    // Switching company is a live server call (POST .../companies/switch) with no offline
    // queuing — unlike TU/BP/Product creates, there's no outbox path for it. Same
    // disabled + "Needs a connection" treatment used everywhere else offline-gated, adapted to
    // PrimeNG MenuItem (no title/tooltip support the way a button has, so the hint is appended
    // to the label instead) — centralized here since both header and sidebar call this same
    // builder, rather than duplicating the check in each caller.
    const offline = !this.networkStatusService.isOnline;
    const offlineHint = offline ? ` (${this.translationService.instant('offline.unavailableTitle')})` : '';
    return [
      { label: sectionLabel, disabled: true, styleClass: 'tms-menu-section-label' },
      ...memberships.map((m) => {
        const name = this.escapeHtml(this.getDisambiguatedName(memberships, m)) + this.escapeHtml(offlineHint);
        const avatar = this.buildCompanyAvatarHtml(m);
        return {
          label: avatar + name,
          escape: false,
          icon: m.isActive ? 'pi pi-fw pi-check' : 'pi pi-fw pi-circle',
          styleClass: 'tms-menu-company-item' + (m.isActive ? ' tms-menu-company-item-active' : ''),
          disabled: offline && !m.isActive,
          command: () => { if (!offline) onSwitch(m.companyId); },
        };
      }),
    ];
  }

  /**
   * Small rounded-square initial avatar per company (same deterministic color-hash approach
   * as BusinessPartnerListComponent.getAvatarColor(), reused here for the same "distinct but
   * consistent" visual language), with the own/home company additionally getting a small
   * gold crown badge overlaid on its corner — replaces an earlier plain inline star icon
   * with the avatar+corner-badge look already established for Business Partners
   * (.bp-avatar/.bp-owner-crown). All styling is inlined (not a CSS class) so it renders
   * identically wherever this shared builder is consumed (header avatar menu, sidebar
   * drawer) without depending on two separate component stylesheets staying in sync.
   */
  private buildCompanyAvatarHtml(m: CompanyMembership): string {
    const initial = this.escapeHtml((m.companyName || '?').trim().charAt(0).toUpperCase() || '?');
    const gradients = [
      'linear-gradient(135deg,#1684ac,#1fa39a)',
      'linear-gradient(135deg,#06b6d4,#0891b2)',
      'linear-gradient(135deg,#10b981,#059669)',
      'linear-gradient(135deg,#f59e0b,#d97706)',
      'linear-gradient(135deg,#ef4444,#dc2626)',
      'linear-gradient(135deg,#ec4899,#db2777)',
      'linear-gradient(135deg,#8b5cf6,#7c3aed)',
      'linear-gradient(135deg,#14b8a6,#0d9488)',
    ];
    const bg = gradients[(m.companyName || '').charCodeAt(0) % gradients.length || 0];
    const crown = m.isDefault
      ? `<span style="position:absolute;top:-4px;right:-4px;width:.8rem;height:.8rem;border-radius:50%;background:linear-gradient(135deg,#f59e0b,#d97706);display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 2px var(--surface-overlay,#1e1e2d);" title="Your company"><i class="pi pi-star-fill" style="font-size:.4rem;color:#fff;"></i></span>`
      : '';
    return `<span style="position:relative;display:inline-flex;align-items:center;justify-content:center;width:1.6rem;height:1.6rem;border-radius:.45rem;background:${bg};color:#fff;font-size:.7rem;font-weight:700;flex-shrink:0;margin-right:.55rem;vertical-align:middle;">${initial}${crown}</span>`;
  }

  private escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
